import { apiClient } from './client';
import type {
  VisibilityReportEnvelope,
  VisibilityBrandRow,
  VisibilityEngineRow,
  VisibilityChatListItem,
  VisibilityChatDetail,
  VisibilityBrandTrendRow,
  VisibilityCostPoint,
  VisibilityTopicHeatmap,
} from '@sequesto-os/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Reports
// ─────────────────────────────────────────────────────────────────────────────

export async function getBrandReport(params: {
  from: string;
  to: string;
  engineIds?: string;
  order_by?: string;
  order_dir?: 'asc' | 'desc';
  limit?: number;
}) {
  return apiClient.get<VisibilityReportEnvelope>('/visibility/report/brand', { params });
}

export async function getDomainReport(params: {
  from: string;
  to: string;
  engineIds?: string;
  domain_class?: string;
  gap?: boolean;
  limit?: number;
}) {
  return apiClient.get<VisibilityReportEnvelope>('/visibility/report/domain', { params });
}

export async function getUrlReport(params: {
  from: string;
  to: string;
  engineIds?: string;
  url_classification?: string;
  gap?: boolean;
  limit?: number;
}) {
  return apiClient.get<VisibilityReportEnvelope>('/visibility/report/url', { params });
}

export async function getBrandTrend(params: { from: string; to: string; brandIds?: string }) {
  return apiClient.get<{ items: VisibilityBrandTrendRow[] }>('/visibility/report/brand-trend', { params });
}

export async function getCostSummary(params: { from: string; to: string }) {
  return apiClient.get<{ items: VisibilityCostPoint[] }>('/visibility/costs', { params });
}

export async function getTopicHeatmap(params: { from: string; to: string }) {
  return apiClient.get<VisibilityTopicHeatmap>('/visibility/report/topic-heatmap', { params });
}

// ─────────────────────────────────────────────────────────────────────────────
// Chats
// ─────────────────────────────────────────────────────────────────────────────

export async function listVisibilityChats(params: {
  from?: string;
  to?: string;
  probeId?: string;
  provider?: string;
  cited?: boolean;
  limit?: number;
}) {
  return apiClient.get<{ items: VisibilityChatListItem[] }>('/visibility/chats', { params });
}

export async function getVisibilityChat(id: string) {
  return apiClient.get<VisibilityChatDetail>(`/visibility/chats/${id}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// Catalog
// ─────────────────────────────────────────────────────────────────────────────

export async function listVisibilityBrands() {
  return apiClient.get<{ items: VisibilityBrandRow[] }>('/visibility/brands');
}

export async function listVisibilityEngines() {
  return apiClient.get<{ items: VisibilityEngineRow[] }>('/visibility/engines');
}

export async function upsertVisibilityBrand(data: {
  name: string;
  domains: string[];
  aliases: string[];
  isOwn: boolean;
  isActive: boolean;
}) {
  return apiClient.post<{ brand: VisibilityBrandRow }>('/visibility/admin/brand', data);
}

export async function listVisibilityTags() {
  return apiClient.get<{ items: { id: string; name: string }[] }>('/visibility/tags');
}

export async function getProbeTags(probeId: string) {
  return apiClient.get<{ tags: { id: string; name: string }[] }>(`/visibility/probes/${probeId}/tags`);
}

export async function setProbeTags(probeId: string, tagIds: string[]) {
  return apiClient.post<{ probeId: string; tagIds: string[] }>(`/visibility/probes/${probeId}/tags`, { tagIds });
}

export interface VisibilitySeedResult {
  brands: number;
  engines: number;
  tags: number;
  probesInserted: number;
  clustersCreated: number;
  probesClustered: number;
  probeTagsCreated: number;
}

export async function seedVisibility() {
  return apiClient.post<VisibilitySeedResult>('/visibility/admin/seed');
}
